/** The install prompt of the browser, kept from the app start so that a button can show it later. */

import { useEffect, useState } from "react";

interface InstallPrompt extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export const NOTE_KEY = "install-note-hidden-until";
export const NOT_NOW_MS = 30 * 24 * 3_600_000;
export const NO_EVENT_MS = 3_000;

/** What the app shows: the install button, one of the how-to texts, or nothing. */
export type InstallWay = "button" | "share" | "menu" | "chrome" | "installed" | "cannot" | "none";

let kept: InstallPrompt | null = null;
let installed = false;
let watching = false;
let waited = false;
let related: number | undefined;
let logged: InstallWay | undefined;
let timer: ReturnType<typeof setTimeout> | undefined;
const listeners = new Set<() => void>();
const tell = () => {
  if (waited) logWay();
  listeners.forEach((listener) => listener());
};

function logWay(): void {
  const way = currentWay();
  if (way === logged) return;
  console.info(
    logged === undefined
      ? `install: way ${way}, standalone ${standalone()}, event ${kept !== null}, sends event ${sendsInstallEvent()}, related apps ${related ?? "unknown"}, ios ${iosKind()}, wait ${NO_EVENT_MS} ms`
      : `install: way ${logged} -> ${way}, event ${kept !== null}, related apps ${related ?? "unknown"}`,
  );
  logged = way;
}

export function standalone(): boolean {
  const nav = navigator as Navigator & { standalone?: boolean };
  return (typeof matchMedia === "function" && matchMedia("(display-mode: standalone)").matches) || nav.standalone === true;
}

/** Each browser on iOS installs from the Share menu; iPadOS gives a Mac agent text with touch. */
export const isIos = (agent: string, touchPoints = 0) => /iPad|iPhone|iPod/.test(agent) || (/Macintosh/.test(agent) && touchPoints > 1);

/** Chromium has the `onbeforeinstallprompt` property; Firefox and Safari do not. */
export const sendsInstallEvent = () => typeof window !== "undefined" && "onbeforeinstallprompt" in window;

/** Chrome on Android; Samsung Internet, Edge, Opera and Yandex have other menus. */
export const isAndroidChrome = (agent: string) => /Android/.test(agent) && /Chrome\//.test(agent) && !/SamsungBrowser|EdgA|OPR|YaBrowser|Firefox/.test(agent);

export function installWay(state: {
  standalone: boolean;
  event: boolean;
  sendsEvent: boolean;
  waited: boolean;
  agent: string;
  touchPoints?: number;
  related?: number;
}): InstallWay {
  if (state.standalone) return "none";
  if (state.event) return "button";
  if (isIos(state.agent, state.touchPoints)) return "share";
  if (!state.waited) return "none";
  // Chrome on Android sends no event when this app is on the phone or cannot install yet.
  if (state.sendsEvent) return isAndroidChrome(state.agent) ? (state.related ? "installed" : "chrome") : "none";
  return /Android/.test(state.agent) && /Firefox\//.test(state.agent) ? "menu" : "cannot";
}

/** Each iOS browser is WebKit with no install event; Chrome has Share in the address bar. */
export type IosBrowser = "safari" | "chrome" | "other";

export const iosBrowser = (agent: string): IosBrowser =>
  /CriOS\//.test(agent) ? "chrome" : /Version\/[\d.]+.*Safari\//.test(agent) && !/FxiOS|EdgiOS|OPiOS|GSA\//.test(agent) ? "safari" : "other";

export const IOS_HOW_TO: Record<IosBrowser, string> = {
  safari: "To install the app, tap Share, then Add to Home Screen.",
  chrome: "To install the app, tap Share in the address bar, then Add to Home Screen.",
  other: "To install the app, open this page in Safari. Tap Share, then Add to Home Screen.",
};

export const HOW_TO: Record<"share" | "menu" | "chrome" | "installed" | "cannot", string> = {
  share: IOS_HOW_TO.safari,
  menu: "To install, open the browser menu (⋮). Select More, then select Add app to Home screen. If the menu has Install, select it.",
  chrome:
    "To install the app, open the Chrome menu (⋮). Select Add to home screen, then select Install. If the menu has Open app, the app is on this phone. Open it from the home screen.",
  installed: "Xflow is on this phone. Open it from the home screen.",
  cannot: "This browser cannot install the app. The app continues to work offline in this tab. To install it, open it in Chrome or Edge.",
};

/** The how-to text of a way; on iOS it names the steps of this browser. */
export const howTo = (way: keyof typeof HOW_TO, agent: string): string => (way === "share" ? IOS_HOW_TO[iosBrowser(agent)] : HOW_TO[way]);

/** A phone-size screen or a touch screen, as the CSS of the app decides it. */
export const PHONE_OR_TOUCH = "(max-width: 40rem), not ((hover: hover) and (pointer: fine))";

/** The note gives the Install app line only on a phone; a desktop has the header button. The Chrome texts are only in the avatar menu. */
export const noteWay = (way: InstallWay, phone: boolean): InstallWay =>
  (way === "button" && !phone) || way === "chrome" || way === "installed" ? "none" : way;

/** The ways that give an Install app item in the avatar menu; iOS has the header button. */
export const menuWay = (way: InstallWay): way is "menu" | "chrome" | "installed" => way === "menu" || way === "chrome" || way === "installed";

export function currentWay(): InstallWay {
  if (typeof navigator === "undefined") return "none";
  return installWay({
    standalone: installed || standalone(),
    event: kept !== null,
    sendsEvent: sendsInstallEvent(),
    waited,
    agent: navigator.userAgent ?? "",
    touchPoints: navigator.maxTouchPoints ?? 0,
    related,
  });
}

export function watchInstall(target: EventTarget = window): void {
  if (watching) return;
  watching = true;
  timer = setTimeout(() => {
    waited = true;
    tell();
  }, NO_EVENT_MS);
  void askRelatedApps();
  target.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    kept = event as InstallPrompt;
    tell();
  });
  target.addEventListener("appinstalled", () => {
    installed = true;
    kept = null;
    tell();
  });
}

function iosKind(): IosBrowser | "no" {
  const agent = navigator.userAgent ?? "";
  return isIos(agent, navigator.maxTouchPoints ?? 0) ? iosBrowser(agent) : "no";
}

/** How many installed apps the manifest's `related_applications` matches; Chromium only. */
async function askRelatedApps(): Promise<void> {
  const nav = navigator as Navigator & { getInstalledRelatedApps?: () => Promise<unknown[]> };
  if (typeof nav.getInstalledRelatedApps !== "function") return;
  try {
    related = (await nav.getInstalledRelatedApps()).length;
  } catch (error) {
    console.info(`install: related apps query failed: ${error instanceof Error ? error.name : "unknown"}`);
    return;
  }
  console.info(`install: related apps ${related}`);
  tell();
}

export const canInstall = () => kept !== null && !installed && !standalone();

export async function promptInstall(): Promise<void> {
  const event = kept;
  if (!event) return;
  kept = null;
  await event.prompt();
  if ((await event.userChoice).outcome === "accepted") installed = true;
  else kept = event;
  tell();
}

export function noteHidden(now: number = Date.now()): boolean {
  try {
    return Number(localStorage.getItem(NOTE_KEY) ?? 0) > now;
  } catch {
    return false;
  }
}

export function hideNote(now: number = Date.now()): void {
  try {
    localStorage.setItem(NOTE_KEY, String(now + NOT_NOW_MS));
  } catch {
    // Without storage, the note shows again at the next start.
  }
}

/** `can`: the button shows; `way`: which note shows in the note place; `agent`: for the iOS steps. */
export function useInstall(): { can: boolean; way: InstallWay; agent: string } {
  const [way, setWay] = useState(currentWay);
  useEffect(() => {
    const update = () => setWay(currentWay());
    listeners.add(update);
    update();
    return () => {
      listeners.delete(update);
    };
  }, []);
  return { can: way === "button", way, agent: typeof navigator === "undefined" ? "" : (navigator.userAgent ?? "") };
}

/** For tests: forget the kept event and the install. */
export function resetInstallForTests(): void {
  kept = null;
  installed = false;
  watching = false;
  waited = false;
  related = undefined;
  logged = undefined;
  clearTimeout(timer);
  listeners.clear();
}
